import {
  AXIS_HEIGHT,
  CELL_GAP,
  COLUMN_HEADER_HEIGHT,
  GRID_GAP,
  GROUP_COL_INNER_GAP,
  ROW_HEADER_WIDTH,
} from './constants';

export type RowHeaderOrientation = 'vertical' | 'horizontal';

export type LeadingColumns = {
  hasRowHeaders: boolean;
  hasGroupHeaders: boolean;
  hasAxisLabelRow: boolean;
  rowHeaderOrientation: RowHeaderOrientation;
  rowHeadersWidth: number;
};

export const getSubRowHeaderWidth = (
  rowHeaderOrientation: RowHeaderOrientation,
  rowHeadersWidth: number,
): number =>
  rowHeaderOrientation === 'vertical' ? ROW_HEADER_WIDTH.vertical.value : rowHeadersWidth;

export const getGroupColumnWidth = (hasGroupHeaders: boolean): number =>
  hasGroupHeaders ? ROW_HEADER_WIDTH.vertical.value : 0;

export const getGroupColumnOffset = (hasGroupHeaders: boolean): number =>
  hasGroupHeaders ? getGroupColumnWidth(hasGroupHeaders) + GROUP_COL_INNER_GAP.value : 0;

export const getRowHeaderColumnWidth = ({
  hasGroupHeaders,
  rowHeaderOrientation,
  rowHeadersWidth,
}: Pick<LeadingColumns, 'hasGroupHeaders' | 'rowHeaderOrientation' | 'rowHeadersWidth'>): number =>
  getGroupColumnOffset(hasGroupHeaders) +
  getSubRowHeaderWidth(rowHeaderOrientation, rowHeadersWidth);

/** An absent column gets no track, so no dangling grid gap is left on the left edge. */
export const getLeadingColumnWidths = (columns: LeadingColumns): number[] => {
  const widths: number[] = [];
  if (columns.hasAxisLabelRow) widths.push(AXIS_HEIGHT.value);
  if (columns.hasRowHeaders) widths.push(getRowHeaderColumnWidth(columns));
  return widths;
};

export const getGridOriginX = (columns: LeadingColumns): number =>
  getLeadingColumnWidths(columns).reduce((origin, width) => origin + width + GRID_GAP.value, 0);

export const getLeadingRowHeights = (hasAxisLabelColumn: boolean): number[] =>
  hasAxisLabelColumn
    ? [AXIS_HEIGHT.value, COLUMN_HEADER_HEIGHT.value]
    : [COLUMN_HEADER_HEIGHT.value];

export const getColumnHeaderBlockHeight = (hasAxisLabelColumn: boolean): number =>
  hasAxisLabelColumn
    ? COLUMN_HEADER_HEIGHT.value + GRID_GAP.value + AXIS_HEIGHT.value
    : COLUMN_HEADER_HEIGHT.value;

export const getGridOriginY = (hasAxisLabelColumn: boolean): number =>
  getColumnHeaderBlockHeight(hasAxisLabelColumn) + GRID_GAP.value;

/** Sticks under the axis label row, not at the end of the header block. */
export const getStuckColumnHeaderTop = (hasAxisLabelColumn: boolean): number =>
  hasAxisLabelColumn ? COLUMN_HEADER_HEIGHT.value + GRID_GAP.value : 0;

export const getStuckColumnHeaderBottom = (hasAxisLabelColumn: boolean): number =>
  getStuckColumnHeaderTop(hasAxisLabelColumn) + COLUMN_HEADER_HEIGHT.value;

/** Uses the vertical row-header width, not `AXIS_HEIGHT`, to match the rendered offset. */
export const getStuckRowHeaderLeft = (hasAxisLabelRow: boolean): number =>
  hasAxisLabelRow ? GRID_GAP.value + ROW_HEADER_WIDTH.vertical.value : 0;

/** Group headers sit in the gutter, so they take no grid row space. */
export const getGroupHeadersOffset = (
  rowIndex: number,
  getGroupHeaderHeight: (index: number) => number | undefined,
): number => {
  let offset = 0;
  for (let index = 0; index < rowIndex; index++) {
    const height = getGroupHeaderHeight(index);
    if (height !== undefined) offset += height + CELL_GAP.value;
  }
  return offset;
};

/** `offsets[i]` is the group-header space before row `i`; the last entry is the total. */
export const getGroupHeaderOffsets = (
  rowCount: number,
  getGroupHeaderHeight: (index: number) => number | undefined,
): number[] => {
  const offsets = [0];
  for (let index = 0; index < rowCount; index++) {
    const height = getGroupHeaderHeight(index);
    offsets.push((offsets[index] ?? 0) + (height === undefined ? 0 : height + CELL_GAP.value));
  }
  return offsets;
};
